import { Button } from "@/components/ui/button"
import type { User } from "@shared/schema"
import { useQuery } from "@tanstack/react-query"
import { Building2, Menu, MessageCircle, X } from "lucide-react"
import { useState } from "react"
import { Link, useLocation } from "wouter"
import { ThemeToggle } from "./ThemeToggle"

export function Header() {
  const [location, setLocation] = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Check if user is logged in
  const { data: user } = useQuery<User>({
    queryKey: ["/api/auth/me"],
    retry: false,
  })

  const isActive = (path: string) => location === path

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <nav className="container max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link
            href="/"
            className="flex items-center gap-2 hover-elevate rounded-md px-3 py-2 transition-colors"
            data-testid="link-home"
          >
            <Building2 className="h-6 w-6 text-primary" />
            <span className="font-bold text-lg">PH Property Finders</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            <Button
              variant={isActive("/browse") ? "secondary" : "ghost"}
              className="hover-elevate"
              asChild
            >
              <Link href="/browse" data-testid="link-browse">
                Browse
              </Link>
            </Button>
            <Button
              variant={isActive("/pricing") ? "secondary" : "ghost"}
              className="hover-elevate"
              asChild
            >
              <Link href="/pricing" data-testid="link-pricing">
                Pricing
              </Link>
            </Button>
            {user && (
              <>
                <Button
                  variant={isActive("/post-property") ? "secondary" : "ghost"}
                  className="hover-elevate"
                  asChild
                >
                  <Link href="/post-property" data-testid="link-post-property">
                    Post Property
                  </Link>
                </Button>
                <Button
                  variant={isActive("/dashboard") ? "secondary" : "ghost"}
                  className="hover-elevate"
                  asChild
                >
                  <Link href="/dashboard" data-testid="link-dashboard">
                    Dashboard
                  </Link>
                </Button>
                <Button
                  variant={isActive("/messages") ? "secondary" : "ghost"}
                  className="hover-elevate"
                  asChild
                >
                  <Link href="/messages" data-testid="link-messages">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Messages
                  </Link>
                </Button>
              </>
            )}
          </div>

          {/* Auth Buttons & Theme Toggle */}
          <div className="hidden md:flex items-center gap-2">
            <ThemeToggle />
            {user ? (
              <Button
                variant="default"
                className="hover-elevate active-elevate-2"
                asChild
              >
                <Link href="/dashboard" data-testid="link-user-profile">
                  {user.fullName}
                </Link>
              </Button>
            ) : (
              <>
                <Button variant="ghost" className="hover-elevate" asChild>
                  <Link href="/login" data-testid="link-login">
                    Login
                  </Link>
                </Button>
                <Button
                  variant="default"
                  className="hover-elevate active-elevate-2"
                  asChild
                >
                  <Link href="/register" data-testid="link-register">
                    Sign Up
                  </Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            <ThemeToggle />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="button-mobile-menu"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t">
            <div className="flex flex-col gap-2">
              <Button
                variant={isActive("/browse") ? "secondary" : "ghost"}
                className="w-full justify-start hover-elevate"
                onClick={() => setMobileMenuOpen(false)}
                asChild
              >
                <Link href="/browse" data-testid="link-browse-mobile">
                  Browse
                </Link>
              </Button>
              <Button
                variant={isActive("/pricing") ? "secondary" : "ghost"}
                className="w-full justify-start hover-elevate"
                onClick={() => setMobileMenuOpen(false)}
                asChild
              >
                <Link href="/pricing" data-testid="link-pricing-mobile">
                  Pricing
                </Link>
              </Button>
              {user ? (
                <>
                  <Button
                    variant={isActive("/post-property") ? "secondary" : "ghost"}
                    className="w-full justify-start hover-elevate"
                    onClick={() => setMobileMenuOpen(false)}
                    asChild
                  >
                    <Link
                      href="/post-property"
                      data-testid="link-post-property-mobile"
                    >
                      Post Property
                    </Link>
                  </Button>
                  <Button
                    variant={isActive("/dashboard") ? "secondary" : "ghost"}
                    className="w-full justify-start hover-elevate"
                    onClick={() => setMobileMenuOpen(false)}
                    asChild
                  >
                    <Link href="/dashboard" data-testid="link-dashboard-mobile">
                      Dashboard
                    </Link>
                  </Button>
                  <Button
                    variant={isActive("/messages") ? "secondary" : "ghost"}
                    className="w-full justify-start hover-elevate"
                    onClick={() => setMobileMenuOpen(false)}
                    asChild
                  >
                    <Link href="/messages" data-testid="link-messages-mobile">
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Messages
                    </Link>
                  </Button>
                  <Button
                    variant="default"
                    className="w-full justify-start hover-elevate active-elevate-2"
                    onClick={() => setMobileMenuOpen(false)}
                    asChild
                  >
                    <Link
                      href="/dashboard"
                      data-testid="link-user-profile-mobile"
                    >
                      {user.fullName}
                    </Link>
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    className="w-full justify-start hover-elevate"
                    onClick={() => setMobileMenuOpen(false)}
                    asChild
                  >
                    <Link href="/login" data-testid="link-login-mobile">
                      Login
                    </Link>
                  </Button>
                  <Button
                    variant="default"
                    className="w-full justify-start hover-elevate active-elevate-2"
                    onClick={() => setMobileMenuOpen(false)}
                    asChild
                  >
                    <Link href="/register" data-testid="link-register-mobile">
                      Sign Up
                    </Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  )
}
